import { keyBy } from 'lodash';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { ViewApI } from 'api/views';
import { ViewType } from 'api/views/types';
import { Switch } from 'components/atoms/switch/switch';
import { FilterBar } from 'components/molecules/filter-bar';
import { Filter } from 'components/molecules/filters/types';
import {
  getViewTranformer,
  SavedViews as ViewsComponent,
  Views,
  FilterPopover,
} from 'components/molecules/views';
import { empty, load } from 'components/molecules/views/core';
import { useCurrentViewWithLocalStorage } from 'components/molecules/views/current-view-state';
import { useViewActions } from 'components/molecules/views/view-actions';
import { FilterableField } from 'components/organisms/tasks/fields';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useTranslate } from 'hooks/use-translation';
import { getDashboardViewsQueryKey } from 'queries/dashboard';
import { queueMacrotask } from 'utils/macrotask';
import { TOGGLE_CHART_LABEL_LOCALSTORAGE_KEY } from '../components/common';
import {
  ApiDashboardFiltersViewDefinition,
  DashboardFiltersViewDefinition,
  DEFAULT_VIEW_ID,
  getDefaultViews,
  getViewDefinitionTransformer,
  isValidViewDefinition,
  viewActionWithSourceId,
  viewDefinitionComparator,
} from './dashboard-filter-utils';
import { DashboardSavedFiltersSelect } from './filter-dropdown';

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-03);
`;

interface DashboardFilterProps {
  clearFilter: () => void;
  fields: FilterableField[];
  onChange: (next: Filter) => void;
  filter: Filter;
  loading: boolean;
  dashboardId: number;
}

export const DashBoardFilter: FC<DashboardFilterProps> = ({
  clearFilter,
  fields,
  filter,
  onChange,
  loading,
  dashboardId,
}) => {
  const translate = useTranslate();
  const [showLabels, setShowLabels] = useLocalStorage(
    TOGGLE_CHART_LABEL_LOCALSTORAGE_KEY,
    false,
  );

  const [storedViewId] = useLocalStorage(
    `dashboards.${dashboardId}.view_id`,
    DEFAULT_VIEW_ID.toString(),
  );

  const [dashboardViews, setDashboardViews] =
    useState<Views<DashboardFiltersViewDefinition>>(empty);

  const dashboardFilteredViews = useMemo(() => {
    if (dashboardViews.ids[0] !== 'just') {
      return dashboardViews;
    }

    const viewsMap: Views<DashboardFiltersViewDefinition>['byIds'] =
      Object.entries(dashboardViews.byIds).reduce((acc, [viewId, viewData]) => {
        if (
          viewData[0] === 'just' &&
          viewData[1].viewSourceId === dashboardId
        ) {
          acc[viewId] = viewData;
        }

        return acc;
      }, {});

    if (!Object.keys(viewsMap).length) {
      return empty;
    }

    return load(Object.values(viewsMap).map((view) => view[1]));
  }, [dashboardId, dashboardViews]);

  const viewDefinitionTransformer = useMemo(
    () => getViewDefinitionTransformer(keyBy(fields, 'fieldName')),
    [fields],
  );
  const viewTransformer = useMemo(
    () => getViewTranformer(viewDefinitionTransformer),
    [viewDefinitionTransformer],
  );
  const defaultViews = useMemo(() => getDefaultViews('Dashboard'), []);

  useQuery(
    getDashboardViewsQueryKey(),
    () =>
      ViewApI.getAllViews<ApiDashboardFiltersViewDefinition>(
        ViewType.Dashboard,
      ),
    {
      onSuccess: (data) => {
        setDashboardViews(load(data.map(viewTransformer.from)));
      },
      onError: () => {
        setDashboardViews(empty);
      },
      refetchOnMount: true,
    },
  );

  const { onViewCreate, onViewUpdate, onViewDelete } = useViewActions({
    views: dashboardFilteredViews,
    setViews: setDashboardViews,
    viewType: ViewType.Dashboard,
    transformer: viewDefinitionTransformer,
  });

  const { viewId: currentViewId, setViewId: setCurrentViewId } =
    useCurrentViewWithLocalStorage({
      defaultViewId: DEFAULT_VIEW_ID,
      loading: dashboardFilteredViews.ids[0] === 'loading',
      localStorageKey: `dashboards.${dashboardId}.view_id`,
    });

  const onViewSelect = useCallback(
    ({ viewId, viewDefinition }) => {
      const nextFilter = (viewDefinition as DashboardFiltersViewDefinition)
        .filter;

      if (nextFilter) {
        onChange(nextFilter);
      } else {
        clearFilter();
      }
      queueMacrotask(() => setCurrentViewId(viewId, true));
    },
    [clearFilter, onChange, setCurrentViewId],
  );

  useEffect(() => {
    const savedId = parseInt(storedViewId, 10);

    const intialFilter =
      dashboardFilteredViews?.byIds?.[currentViewId]?.[1]?.viewDefinition
        ?.filter;

    if (intialFilter) {
      onChange(intialFilter);

      return;
    }

    if (savedId !== DEFAULT_VIEW_ID) {
      const fallbackFilter =
        dashboardFilteredViews?.byIds?.[savedId]?.[1]?.viewDefinition?.filter;

      if (fallbackFilter) {
        onChange(fallbackFilter);
        queueMacrotask(() => setCurrentViewId(savedId, true));
      } else {
        queueMacrotask(() => {
          setCurrentViewId(DEFAULT_VIEW_ID, false);
          clearFilter();
        });
      }
    }
  }, [
    clearFilter,
    currentViewId,
    dashboardFilteredViews?.byIds,
    onChange,
    setCurrentViewId,
    storedViewId,
  ]);

  return (
    <FilterBar>
      <FilterBar.Primary>
        <ViewsComponent
          filterComponent={
            <FilterPopover
              filter={filter}
              fields={fields}
              onChange={onChange}
              loading={loading}
            />
          }
          additionalComponent={DashboardSavedFiltersSelect}
          additionComponentProps={{
            setViewId: setCurrentViewId,
            setFilter: onChange,
            views: dashboardFilteredViews,
            selectedViewId: currentViewId,
            clearFilter,
            dashboardId,
          }}
          views={dashboardFilteredViews}
          defaultViewsList={defaultViews}
          defaultViewId={DEFAULT_VIEW_ID}
          currentViewDefinition={{
            filter,
          }}
          onViewSelect={onViewSelect}
          isValidViewDefinition={isValidViewDefinition}
          viewDefinitionComparator={viewDefinitionComparator}
          onViewCreate={viewActionWithSourceId(onViewCreate, dashboardId)}
          onViewDelete={onViewDelete}
          onViewUpdate={onViewUpdate}
          selectedViewId={currentViewId ?? DEFAULT_VIEW_ID}
          includeSavedViews={false}
          isDashboardView
        />
      </FilterBar.Primary>
      <FilterBar.Secondary>
        <SwitchWrapper>
          <Switch size="small" checked={showLabels} onChange={setShowLabels} />
          <div>{translate('dashboard.widgets.show.labels')}</div>
        </SwitchWrapper>
      </FilterBar.Secondary>
    </FilterBar>
  );
};
